'use client'

import Bowser from 'bowser'
import { useSearchParams } from 'next/navigation'
import { Suspense, useEffect } from 'react'

import {
	deleteCookieClient,
	getAllCookiesClient,
	getCookieClient,
	setCookieClient,
} from '@/utils/client'

const getClickId = (params: URLSearchParams): string | undefined => {
	const keys = [
		'gclid', // Google Ads (Standard tracking)
		'gbraid', // Google Ads (iOS, app-to-web tracking when GCLID is blocked)
		'wbraid', // Google Ads (iOS, web-to-web tracking when GCLID is blocked)
		'msclkid', // Microsoft Ads
		'fbclid', // Facebook Ads
	]
	const foundKey = keys.find((key) => params.has(key))
	return foundKey ? `${foundKey}_${params.get(foundKey)}` : undefined
}

const inferDeviceVendor = (model: string | undefined): string => {
	if (!model) return 'unknown'

	const modelLower = model.toLowerCase()

	if (
		modelLower.includes('iphone') ||
		modelLower.includes('ipad') ||
		modelLower.includes('ipod')
	) {
		return 'Apple'
	}
	if (modelLower.startsWith('sm-') || modelLower.includes('samsung')) {
		return 'Samsung'
	}
	if (modelLower.includes('pixel')) {
		return 'Google'
	}
	if (modelLower.includes('oneplus')) {
		return 'OnePlus'
	}
	if (modelLower.includes('xiaomi') || modelLower.includes('redmi')) {
		return 'Xiaomi'
	}
	if (modelLower.includes('huawei') || modelLower.includes('honor')) {
		return 'Huawei'
	}

	return 'unknown'
}

const getClientHintsInfo = async (): Promise<
	Partial<{
		device_model: string
		device_vendor: string
		os_name: string
		os_version: string
		browser_name: string
		browser_version: string
	}>
> => {
	if (typeof navigator === 'undefined' || !('userAgentData' in navigator)) {
		return {}
	}

	try {
		const uaData = navigator.userAgentData
		if (!uaData) {
			return {}
		}
		const highEntropyValues = await uaData.getHighEntropyValues([
			'model',
			'platform',
			'platformVersion',
			'architecture',
			'bitness',
			'fullVersionList',
		])

		const browserBrand = uaData.brands?.[0]?.brand || 'unknown'
		const browserVersion =
			highEntropyValues.fullVersionList?.[0]?.version ||
			uaData.brands?.[0]?.version ||
			'unknown'

		return {
			device_model: highEntropyValues.model || 'unknown',
			device_vendor: inferDeviceVendor(highEntropyValues.model),
			os_name: highEntropyValues.platform || 'unknown',
			os_version: highEntropyValues.platformVersion || 'unknown',
			browser_name: browserBrand,
			browser_version: browserVersion,
		}
	} catch (error) {
		console.warn('Failed to get User Agent Client Hints:', error)
		return {}
	}
}

const parseUserAgent = (userAgent: string) => {
	const parsed =
		userAgent && typeof navigator !== 'undefined'
			? Bowser.parse(userAgent)
			: null

	return {
		user_agent: userAgent || 'unknown',
		browser_name: parsed?.browser?.name ?? 'unknown',
		browser_version: parsed?.browser?.version ?? 'unknown',
		device_model:
			parsed?.platform?.model ?? parsed?.platform?.type ?? 'unknown',
		device_vendor: parsed?.platform?.vendor ?? 'unknown',
		os_name: parsed?.os?.name ?? 'unknown',
		os_version: parsed?.os?.version ?? 'unknown',
	}
}

const TrackingScriptInner = () => {
	const searchParams = useSearchParams()

	useEffect(() => {
		const prefix = '__tracking_'
		const clickIdKey = 'clid'

		const oldClickId = getCookieClient(`${prefix}${clickIdKey}`)
		const newClickId = getClickId(searchParams)

		const isFirstClick = !oldClickId
		const isNewClick = !!newClickId && oldClickId !== newClickId

		if (isFirstClick || isNewClick) {
			// Drop all old tracking cookies
			const allCookies = getAllCookiesClient()
			Object.keys(allCookies).forEach((cookieKey) => {
				if (cookieKey.startsWith(prefix)) {
					deleteCookieClient(cookieKey)
				}
			})

			// Collect all search params
			const searchParamsObj: Record<string, string> = {}
			searchParams.forEach((value, key) => {
				searchParamsObj[key] = value
			})

			// Parse user agent for debug parameters
			const userAgentString =
				typeof navigator !== 'undefined' ? navigator.userAgent : ''
			const uaInfo = parseUserAgent(userAgentString)

			const setTrackingData = async () => {
				const clientHintsInfo = await getClientHintsInfo()

				const finalUaInfo = {
					...uaInfo,
					...clientHintsInfo,
				}

				// Set new tracking data
				const trackingData: Record<string, string> = {
					...searchParamsObj,
					[clickIdKey]: newClickId || 'none',
					referrer: document?.referrer || 'none',
					href: window?.location?.href || 'unknown',
					tracked_at: String(Date.now()),
					...finalUaInfo,
				}

				Object.entries(trackingData).forEach(([key, value]) => {
					setCookieClient(`${prefix}${key}`, String(value), {
						expires: new Date(Date.now() + 365 * 10 * 24 * 60 * 60 * 1000), // 10 years
					})
				})
			}

			setTrackingData()
		}
	}, [searchParams])

	return null
}

export const TrackingScript = () => {
	return (
		<Suspense fallback={null}>
			<TrackingScriptInner />
		</Suspense>
	)
}
